import { applyMiddleware, combineReducers, compose, createStore, Dispatch, Reducer } from 'redux';
import thunk from 'redux-thunk';
import createSagaMiddleware from 'redux-saga';
import { loginReducer } from '../components/account/state/reducers';
import moderationReducer from '../components/moderation-v2/sharable/moderationReducers';
import navigationReducer from '../components/common/navigation/state';
import { modalsReducer } from '../components/moderation-v2/comments-and-posts/modals/state/reducers';
import { applicationReducer } from './app-state/reducer';
import liveBlogReducer from '../components/features/live-blog-v2/state/reducer';
import rootSaga from './middlewares/saga';
import { State } from './index';

import analyticsOverviewReducer from '@host-panel/analytics/overview/state';
import { moderationFiltersReducer } from '../components/settings/policy/moderation-filters/moderation-filters/moderationFiltersSlice';
import { tenantConfigReducer } from '../tenant-config/tenantConfigSlice';
import { initConfigReducer } from '../init-config/initConfigSlice';
import { generalSettingsReducer } from '../components/preferences/general/generalSettingsSlice';
import { policyFlagsReducer } from '../../setiings/policy/policyFlagsSlice';
import { superAdminReducer } from '../../settings/super-admin/superAdminSlice';
import { showToast, toastAction, toastReducer } from '../../components/toast/toastSlice';
import { broadcastReducer } from '../../features/broadcast/broadcastSlice';
import { monetizationReducer } from '../../analytics/monetization/monetizationSlice';
import { usersReducer } from '../../users/usersSlice';
import { currentUserReducer } from '../../current-user/currentUserSlice';
import { spotSelectorReducer } from '../../spot-selector/spotSelectorSlice';
import { authReducer } from '../../auth/authSlice';
import { ssoReducer } from '../../settings/single-sign-on/ssoSlice';
import { spotsReducer } from '../../spots/spotsSlice';

export function createReducer(): Reducer {
  return combineReducers({
    account: loginReducer,
    auth: authReducer,
    application: applicationReducer,
    analytics: combineReducers({
      overview: analyticsOverviewReducer,
      monetization: monetizationReducer
    }),
    currentUser: currentUserReducer,
    toast: toastReducer,
    moderation: moderationReducer,
    navigation: navigationReducer,
    modals: modalsReducer,
    settings: combineReducers({
      policy: combineReducers({
        moderationFilters: moderationFiltersReducer,
        flags: policyFlagsReducer
      }),
      general: generalSettingsReducer,
      superAdmin: superAdminReducer,
      sso: ssoReducer
    }),
    features: combineReducers({
      liveBlog: liveBlogReducer,
      broadcast: broadcastReducer
    }),
    tenantConfig: tenantConfigReducer,
    initConfig: initConfigReducer,
    users: usersReducer,
    spotSelector: spotSelectorReducer,
    spots: spotsReducer
  });
}

interface RefsStore {
  toastAction?: (dispatch: Dispatch) => void;
}

const refsStore: RefsStore = { toastAction: undefined };

function refsHandler({ dispatch }) {
  return next => action => {
    switch (action.type) {
      case showToast.type:
        const { onAction } = action.payload;
        refsStore.toastAction = onAction;

        break;

      case toastAction.type:
        if (typeof refsStore.toastAction === 'function') {
          refsStore.toastAction(dispatch);
          refsStore.toastAction = undefined;
        }

        break;
    }

    return next(action);
  };
}

export function initializeStore(initialState: Partial<State> = {}, customReducer?: Reducer) {
  const sagaMiddleware = createSagaMiddleware();
  const reducer = customReducer ?? createReducer();
  const middlewares = [sagaMiddleware, thunk, refsHandler];
  const enhancers = [applyMiddleware(...middlewares)];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    process.env.NODE_ENV !== 'production' &&
    typeof window === 'object' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;
  /* eslint-enable */

  const store = createStore(reducer, initialState, composeEnhancers(...enhancers));

  sagaMiddleware.run(rootSaga);

  return store;
}
